import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { ComponentField } from '@/types/DynamicForm.type';
import { countryCodeToCountryName } from '@/utils/countries.util';
import { getQuestionLabel } from '@/utils/dynamicForm.util';
import { useLocationAutocomplete } from '@/hooks/api/search/useLocationAutocomplete';
import FormRadio from '@/components/molecules/form/FormRadio';

type Props = Extract<ComponentField, { fieldType: 'locationSelect' }>;

/**
 * Dynamic form field component for location selection
 * Integrates with useLocationAutocomplete hook and handles country code processing
 * 
 * @param cfg - Field configuration object
 * @returns JSX element for the location select field
 */
const DynamicFormLocationSelect: React.FC<Props> = (cfg) => {
  const { setValue } = useFormContext();

  const { data: includedLocationData } = useLocationAutocomplete({
    query: "",
    ...cfg.config,
  });

  const country = useCallback((countryCode: string) => countryCodeToCountryName(countryCode), []);

  return (
    <FormRadio
      options={includedLocationData || []}
      renderOption={(opt) => (
        <span className="flex flex-1">
          <span className="flex flex-col gap-1">
            <span className="inline-flex items-center gap-2 font-semibold text-primary">
              {country(opt.country_code!)}
            </span>
          </span>
        </span>
      )}
      {...cfg}
      label={getQuestionLabel(cfg)}
      name={cfg.name}
      helperTextBelowLabel
      matchingProperty={"country_code"}
      onChange={() => {
        setValue("REGION_COUNTRY_INCLUDE", []);
        setValue("REGION_COUNTRY_EXCLUDE", []);
      }}
    />
  );
};

export default DynamicFormLocationSelect;